import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  fetchPaygConsent,
  setPayg,
  type PaygAction,
  type PaygBill,
  type PaygFailure,
  type PaygView,
} from "../../lib/api";
import { qk } from "../../lib/queries";
import { roomAdded } from "../../lib/room";
import { Button, LinkButton, SecondaryButton } from "../ui/button";
import { messageFor } from "./plan-area";
import { statedDate } from "./plan";

export const PAYG_TITLE = "Pay as you go";
export const PAYG_ON_ACTION = "Turn on pay as you go";
export const PAYG_RAISE_ACTION = "Raise the maximum";
export const PAYG_CHANGE_ACTION = "Change the maximum";
export const PAYG_OFF_ACTION = "Turn off pay as you go";
export const PAYG_MAX_LABEL = "Monthly maximum";
/** Pay as you go so far this month — the window is a month, and it reads as one. */
export const PAYG_SO_FAR = "So far this month";
export const PAYG_BILLS = "Pay-as-you-go invoices";
export const PAYG_CONFIRM_BANK = "Confirm with your bank";
/** A failed bill with a page to pay it on (ruling R-0923-5 (3)). */
export const PAYG_PAY_BILL = "Pay this invoice";
/**
 * What the confirm says, with the maximum the consent above it names — and,
 * where the maximum is entered including VAT or GST, the label that says so
 * (ruling R-0923-5 (2)).
 */
export function paygSetLabel(
  action: PaygAction,
  maxDisplay: string,
  suffix: string | null = null,
): string {
  const tail = suffix ? ` ${suffix}` : "";
  if (action === "on") return `${PAYG_ON_ACTION} — up to ${maxDisplay} a month${tail}`;
  if (action === "change") return `Set the maximum to ${maxDisplay} a month${tail}`;
  return PAYG_OFF_ACTION;
}

const BILL_KIND: Record<PaygBill["kind"], string> = {
  early: "Reached one month's price",
  remainder: "Window ended",
  quarterly: "Quarter ended",
  cancellation: "Plan ended",
};

const BILL_STATUS: Record<PaygBill["status"], string> = {
  pending: "Billing",
  paid: "Paid",
  failed: "Payment failed",
};

/** "$40" from a whole number of cents of whole dollars — for the confirm's label only. */
function dollars(cents: number): string {
  return `$${Math.round(cents / 100)}`;
}

function failureLine(r: PaygFailure): string {
  if (r.code === "PAYG_UNAVAILABLE") return r.reason;
  if (r.code === "NOT_OFFERED") return "Pay as you go is for paid plans, not for a trial or Free.";
  return messageFor(r.code);
}

/**
 * **PAY AS YOU GO** (ruling R-0923-3; docs/plans.md §3.3, §5.4, §9).
 *
 * ONE COMPONENT, TWO PLACES, AND THEY ARE THE PLACES THE RULING NAMES: billing
 * settings, where it can be turned on, changed or turned off at any time
 * (`settings`), and "Get more usage" at the wall, where it is offered beside
 * the rung above (`offer`) — turning it on, or raising the maximum once it is
 * the maximum that stopped the work. Never in a conversation, the composer or
 * a door page: the dollars on it are billing's alone.
 *
 * CONSENT AT FUNDING, LIKE THE PLAN'S. Every act here is a consent to a
 * sentence the SERVER wrote. The maximum is inside those words, so a changed
 * maximum is a new sentence asked of the server before it is shown — this side
 * never composes a word of it — and the confirm posts back the hash of exactly
 * what was on screen, with the drawdown sentence shown beside it on the way on.
 *
 * NO CAPACITY, EVER (ruling C2). The rate is a relationship between a share of
 * the allowance and the plan's own price; the maximum and what has accrued are
 * the subscriber's own dollars. No quantity of work appears anywhere here.
 */
export function PaygPanel({
  payg,
  mode,
  onChanged,
}: {
  payg: PaygView;
  mode: "offer" | "settings";
  /** Told after a change the server accepted, so the caller can say so. */
  onChanged?: (action: PaygAction) => void;
}) {
  const queryClient = useQueryClient();
  // Which consent is open: turning it on, changing the maximum, turning it off.
  const [open, setOpen] = useState<PaygAction | null>(
    mode === "offer" && !payg.on && payg.available ? "on" : null,
  );
  const startCents = payg.on ? (payg.max_cents ?? payg.default_max_cents) : payg.default_max_cents;
  const [maxDollars, setMaxDollars] = useState(String(Math.round(startCents / 100)));
  const [text, setText] = useState<string | null>(open === "on" ? payg.consent_text : null);
  const [drawdown, setDrawdown] = useState<string | null>(open === "on" ? payg.drawdown : null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cents = Math.round(Number(maxDollars) * 100);
  const valid =
    /^\d+$/.test(maxDollars.trim()) && cents >= payg.min_max_cents && cents <= payg.max_max_cents;

  /**
   * THE SENTENCE FOR THE MAXIMUM ON SCREEN, from the server, a beat after the
   * last keystroke. Until it arrives there is no sentence and no confirm: a
   * button under the previous maximum's words would be a consent to a figure
   * nobody was shown.
   */
  const asked = useRef(0);
  useEffect(() => {
    if (open === null) return;
    if (open === "off") {
      setText(payg.off_text);
      setDrawdown(null);
      return;
    }
    if (!valid) {
      setText(null);
      return;
    }
    if (open === "on" && cents === payg.default_max_cents && !payg.on) {
      setText(payg.consent_text);
      setDrawdown(payg.drawdown);
      return;
    }
    const ticket = ++asked.current;
    setText(null);
    const timer = setTimeout(() => {
      void fetchPaygConsent(open, cents).then((r) => {
        if (ticket !== asked.current) return;
        if (!r.ok) {
          setError(failureLine(r));
          return;
        }
        setText(r.text);
        setDrawdown(r.drawdown);
      });
    }, 250);
    return () => clearTimeout(timer);
  }, [open, cents, valid, payg]);

  async function confirm() {
    if (open === null || text === null) return;
    setBusy(true);
    setError(null);
    const action = open;
    const r = await setPayg({
      action,
      maxCents: action === "off" ? null : cents,
      text,
      drawdown: action === "off" ? null : drawdown,
    });
    setBusy(false);
    if (!r.ok) {
      setError(failureLine(r));
      return;
    }
    setOpen(null);
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: qk.plan() }),
      queryClient.invalidateQueries({ queryKey: qk.me() }),
    ]);
    // Room was added: a conversation waiting at the wall carries on (wave 57).
    if (action !== "off") roomAdded();
    onChanged?.(action);
  }

  const form = open !== null && (
    <div className="mt-3 space-y-2" data-payg-consent={open}>
      {open !== "off" && (
        <label className="block text-[12px] text-muted">
          {PAYG_MAX_LABEL}
          <span className="mt-1 flex items-center gap-1">
            <span className="text-[13px] text-text-soft">$</span>
            <input
              inputMode="numeric"
              aria-label={PAYG_MAX_LABEL}
              className="h-9 w-24 rounded-md border border-border bg-surface px-2 text-[13px]
                         tabular-nums text-text focus-visible:outline-none focus-visible:ring-2
                         focus-visible:ring-focus/70"
              value={maxDollars}
              onChange={(e) => setMaxDollars(e.target.value.replace(/[^\d]/g, "").slice(0, 5))}
            />
            <span className="text-[12px] text-faint">
              a month{payg.amounts_suffix ? ` ${payg.amounts_suffix}` : ""} · from{" "}
              {dollars(payg.min_max_cents)} to {dollars(payg.max_max_cents)}
            </span>
          </span>
        </label>
      )}
      {/* THE SERVER'S SENTENCE, VERBATIM — what the hash goes back as. */}
      {/* ONE ELEMENT PER LINE, with a gap between: the hashed string is the same
          string, and its lines are what the person reads one thought at a time. */}
      <div
        className="space-y-1.5 text-[14px] leading-relaxed text-text-soft sm:text-[13px]"
        data-testid="payg-consent-text"
      >
        {text === null ? (
          <p>{valid ? "…" : "Choose a whole number of dollars in the range above."}</p>
        ) : (
          text.split("\n").map((line, i) => (
            <p key={i} data-payg-line>
              {line}
            </p>
          ))
        )}
      </div>
      {open !== "off" && drawdown && text && (
        <p className="text-[11px] leading-relaxed text-muted" data-testid="payg-drawdown">
          {drawdown}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 pt-1">
        <Button
          type="button"
          className="h-10 px-4"
          disabled={busy || text === null || (open !== "off" && !valid)}
          onClick={() => void confirm()}
          data-payg-confirm
        >
          {paygSetLabel(open, dollars(cents), open === "off" ? null : payg.amounts_suffix)}
        </Button>
        {!(mode === "offer" && open === "on") && (
          <LinkButton type="button" onClick={() => setOpen(null)} disabled={busy}>
            Not now
          </LinkButton>
        )}
      </div>
    </div>
  );

  return (
    <div
      className="mt-4 rounded-r-lg border-l-2 border-brass/70 bg-brass/[0.05] px-4 py-3"
      data-payg={payg.on ? "on" : "off"}
    >
      <p className="text-sm font-medium text-text">{PAYG_TITLE}</p>
      {/* THE RATE ONCE (owner, 2026-09-23): while a consent is open its own
          sentence states the rate, so the line above it would say it twice. */}
      {(open === null || open === "off") && (
        <p className="mt-1 text-[13px] leading-snug text-text-soft">{payg.rate_line}</p>
      )}

      {payg.on ? (
        <>
          <p className="mt-2 text-[13px] text-text-soft">
            On, up to <span className="tabular-nums">{payg.max_display}</span>.{" "}
            <span className="text-muted">
              {PAYG_SO_FAR}: <span className="tabular-nums">{payg.accrued_display}</span>.
            </span>
          </p>
          {open === null && (
            <p className="mt-3 flex flex-wrap gap-2">
              <SecondaryButton type="button" onClick={() => setOpen("change")}>
                {payg.at_maximum ? PAYG_RAISE_ACTION : PAYG_CHANGE_ACTION}
              </SecondaryButton>
              {mode === "settings" && (
                <SecondaryButton type="button" onClick={() => setOpen("off")}>
                  {PAYG_OFF_ACTION}
                </SecondaryButton>
              )}
            </p>
          )}
        </>
      ) : !payg.available ? (
        <p className="mt-2 text-[13px] leading-snug text-text-soft" role="status">
          {payg.unavailable_reason}
        </p>
      ) : (
        open === null && (
          <p className="mt-3">
            <SecondaryButton type="button" onClick={() => setOpen("on")}>
              {PAYG_ON_ACTION}
            </SecondaryButton>
          </p>
        )
      )}

      {form}
      {error && (
        <p className="mt-2 text-[13px] text-danger" role="alert">
          {error}
        </p>
      )}

      {mode === "settings" && payg.bills.length > 0 && <PaygBills bills={payg.bills} />}
    </div>
  );
}

/**
 * THE BILLS, full accounting after (docs/compliance.md): each amount, why it
 * was billed, the window it was for and what the processor did with it. A
 * charge the bank sent back to be confirmed (R-0923-4 (8)) carries the button
 * that opens the processor's own page for it — the email about it lands here.
 */
export function PaygBills({ bills }: { bills: PaygBill[] }) {
  return (
    <div className="mt-4 border-t border-border-soft pt-3" data-payg-bills>
      <p className="text-[11px] uppercase tracking-[0.12em] text-faint">{PAYG_BILLS}</p>
      <ul className="mt-1 divide-y divide-border-soft">
        {bills.map((b) => (
          <li key={b.id} className="flex flex-wrap items-baseline gap-x-3 py-1.5 text-[13px]">
            <span className="tabular-nums text-text">{b.amount_display}</span>
            <span className="text-muted">
              {BILL_KIND[b.kind]}
              {b.on_renewal ? ", on your plan's bill" : ""} · {statedDate(b.window_start)} to{" "}
              {statedDate(b.window_end)}
            </span>
            <span
              className={`ml-auto text-[12px] ${b.status === "failed" ? "text-danger" : "text-faint"}`}
            >
              {BILL_STATUS[b.status]}
            </span>
            {b.status === "failed" && (b.action_url ?? b.pay_url) && (
              <a
                href={b.action_url ?? b.pay_url ?? undefined}
                target="_blank"
                rel="noreferrer"
                className="w-full pt-1 text-[12px] font-medium text-text underline"
              >
                {b.action_url ? PAYG_CONFIRM_BANK : PAYG_PAY_BILL}
              </a>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
